import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, Stack, Chip, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Reveal } from './motion';
import {
  ORANGE,
  ORANGE_DK,
  ORANGE_SOFT,
  BORDER,
  BG_SOFT,
  TXT,
  TXT_DIM,
  primaryBtn,
  gradientText,
  focusRing,
} from './theme';

export function SectionHead({ overline, title }) {
  return (
    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, maxWidth: 720, mx: 'auto' }}>
      <Typography sx={{ color: ORANGE_DK, fontWeight: 700, letterSpacing: 1.6, fontSize: '0.78rem', mb: 1.5 }}>
        {overline.toUpperCase()}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.7rem' }, letterSpacing: -1, lineHeight: 1.1 }}>
        {title}
      </Typography>
    </Box>
  );
}

/* Sticky pill nav. `navItems` is [{ label, href? , onClick? }]. */
export function SiteNav({ navItems = [] }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Scroll-spy: highlight the nav item whose section is currently in view.
  const sectionIds = navItems
    .map((i) => (i.href && i.href.startsWith('#') ? i.href.slice(1) : null))
    .filter(Boolean);
  const [activeId, setActiveId] = useState(() => {
    // On load/refresh, select the section named in the URL hash (e.g. /#faq).
    const h = (typeof window !== 'undefined' ? window.location.hash : '').slice(1);
    return h || '';
  });

  useEffect(() => {
    const els = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (inView[0]) setActiveId(inView[0].target.id);
      },
      // Active "band" sits near the top third of the viewport, below the nav.
      { rootMargin: '-40% 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navItems]);

  return (
    <>
    {/* Fixed so the header stays visible even though the page root uses
        overflow-x:hidden (which would break position:sticky). */}
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100, px: 2, py: 1.5,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(14px)',
      borderBottom: `1px solid ${BORDER}`,
    }}>
      <Container maxWidth="lg" disableGutters>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', px: { xs: 0.5, md: 1 } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                background: `linear-gradient(135deg, ${ORANGE}, #FF8A1E)`,
                boxShadow: '0 4px 14px rgba(255,102,0,0.35)',
              }}
            >
              <ShieldOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, letterSpacing: -0.3, fontSize: '1.05rem' }}>
              ConsentCMS
            </Typography>
          </Stack>
          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              {navItems.map((item) => {
                const isActive = item.href && item.href === `#${activeId}`;
                return (
                  <Button
                    key={item.label}
                    href={item.href}
                    onClick={item.onClick}
                    aria-current={isActive ? 'true' : undefined}
                    sx={{
                      color: isActive ? ORANGE_DK : TXT_DIM,
                      fontWeight: isActive ? 700 : 500,
                      textTransform: 'none',
                      px: 1.5,
                      borderRadius: 1.5,
                      background: isActive ? ORANGE_SOFT : 'transparent',
                      transition: 'color 0.2s ease, background-color 0.2s ease',
                      ...focusRing,
                      '&:hover': { color: isActive ? ORANGE_DK : TXT, background: isActive ? ORANGE_SOFT : 'rgba(15,23,42,0.04)' },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              <Button onClick={() => navigate('/login')} sx={{ color: TXT, fontWeight: 600, ml: 1, textTransform: 'none', ...focusRing }}>
                Sign in
              </Button>
              <Button onClick={() => navigate('/register')} sx={primaryBtn} endIcon={<ArrowForwardIcon />}>
                Get started
              </Button>
            </Stack>
          )}
          {isMobile && (
            <Button onClick={() => navigate('/register')} sx={{ ...primaryBtn, px: 2.5, py: 1 }}>
              Start
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
    {/* Spacer reserves the fixed header's height so content isn't hidden. */}
    <Box aria-hidden sx={{ height: { xs: 74, md: 84 } }} />
    </>
  );
}

/* Reusable closing call-to-action block. */
export function CtaBlock({
  title,
  highlight,
  subtitle,
  primaryLabel = 'Create your account',
  onPrimary,
  secondaryLabel = 'Sign in',
  onSecondary,
}) {
  const navigate = useNavigate();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
      <Reveal>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center',
            p: { xs: 5, md: 9 },
            borderRadius: 4,
            border: '1px solid rgba(255,102,0,0.25)',
            background: 'radial-gradient(120% 120% at 50% 0%, rgba(255,102,0,0.1) 0%, #FFFFFF 60%)',
            boxShadow: '0 16px 50px rgba(255,102,0,0.1)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -130,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 360,
              height: 360,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${ORANGE} 0%, transparent 65%)`,
              filter: 'blur(90px)',
              opacity: 0.2,
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' }, letterSpacing: -1, mb: 2 }}>
              {title} {highlight && <Box component="span" sx={gradientText}>{highlight}</Box>}
            </Typography>
            <Typography sx={{ color: TXT_DIM, fontSize: '1.1rem', mb: 4, maxWidth: 520, mx: 'auto' }}>
              {subtitle}
            </Typography>
            {/* Single primary action; "Sign in" demoted to a quiet text link. */}
            <Stack alignItems="center" spacing={1.5}>
              <Button size="large" sx={primaryBtn} endIcon={<ArrowForwardIcon />} onClick={onPrimary || (() => navigate('/register'))}>
                {primaryLabel}
              </Button>
              <Typography sx={{ color: TXT_DIM, fontSize: '0.9rem' }}>
                Already have an account?{' '}
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={onSecondary || (() => navigate('/login'))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') (onSecondary || (() => navigate('/login')))(); }}
                  sx={{ color: ORANGE_DK, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  {secondaryLabel}
                </Box>
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Reveal>
    </Container>
  );
}

export function SiteFooter() {
  const navigate = useNavigate();
  return (
    <Box sx={{ bgcolor: BG_SOFT, borderTop: `1px solid ${BORDER}`, py: 6 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 4 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  background: `linear-gradient(135deg, ${ORANGE}, #FF8A1E)`,
                }}
              >
                <ShieldOutlinedIcon sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 800 }}>ConsentCMS</Typography>
            </Stack>
            <Typography sx={{ color: TXT_DIM, fontSize: '0.88rem', maxWidth: 320 }}>
              Enterprise-grade consent management for collecting, managing, and auditing Data Principal consent
              in compliance with India’s Digital Personal Data Protection Act, 2023.
            </Typography>
          </Box>
          <FooterCol
            title="Product"
            links={[
              ['Platform', () => navigate('/')],
              ['Readiness checklist', () => navigate('/dpdp-readiness-checklist')],
              ['Penalty calculator', () => navigate('/dpdp-penalty-calculator')],
              ['FAQ', () => navigate('/dpdp-faq')],
              ['Get started', () => navigate('/register')],
            ]}
          />
          <FooterCol
            title="Developers"
            links={[
              ['API reference', () => navigate('/documentation/api')],
              ['Technical overview', () => navigate('/documentation/technical')],
              ['Documentation', () => navigate('/documentation')],
            ]}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.9rem' }}>Compliance</Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {['DPDP Act', 'Data Principal', 'RBAC', 'OAuth2'].map((t) => (
                <Chip key={t} label={t} size="small" sx={{ color: TXT_DIM, background: '#fff', border: `1px solid ${BORDER}` }} />
              ))}
            </Stack>
          </Box>
        </Box>
        <Divider sx={{ my: 4, borderColor: BORDER }} />
        <Typography sx={{ color: TXT_DIM, fontSize: '0.78rem' }}>
          © {new Date().getFullYear()} ConsentCMS. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

function FooterCol({ title, links }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.9rem' }}>{title}</Typography>
      <Stack spacing={1}>
        {links.map(([label, onClick]) => (
          <Typography
            key={label}
            onClick={onClick}
            sx={{ color: TXT_DIM, fontSize: '0.86rem', cursor: 'pointer', width: 'fit-content', '&:hover': { color: TXT } }}
          >
            {label}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

/* Small breadcrumb-style "back to home" used at the top of tool pages. */
export function PageHeader({ overline, title, subtitle }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: { xs: 4, md: 5 } }}>
      <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
        <Chip
          onClick={() => navigate('/')}
          label="← Back to home"
          sx={{
            cursor: 'pointer',
            color: TXT_DIM,
            fontWeight: 600,
            background: BG_SOFT,
            border: `1px solid ${BORDER}`,
            ...focusRing,
            '&:hover': { color: ORANGE_DK, borderColor: 'rgba(255,102,0,0.4)', background: ORANGE_SOFT },
          }}
        />
      </Stack>
      <Typography sx={{ color: ORANGE_DK, fontWeight: 700, letterSpacing: 1.6, fontSize: '0.78rem', mb: 1.5 }}>
        {overline.toUpperCase()}
      </Typography>
      <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.1rem', md: '3rem' }, letterSpacing: -1, lineHeight: 1.1, mb: 2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: TXT_DIM, fontSize: '1.05rem', lineHeight: 1.6 }}>{subtitle}</Typography>
      )}
    </Box>
  );
}
